import * as React from "react";
import { error, bug } from "@flow/lib/error";
import { formatSeconds } from "@flow/lib/formatters";
import { reportVideoRecorderError } from "@flow/common/core_routes";
import * as browser_detect from "@flow/lib/browser_detect";
import { Text } from "@flow/lib/i18n";
import { Box, Button, FormControl, Input, InputLabel, Typography } from "@mui/material";
import { memberTheme } from "../member/memberTheme";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { styled, keyframes } from "@mui/system";

const flashRedAnim = keyframes`
  0% {
    color: red;
  }
  49% {
    color: red;
  }
  50% {
    color: transparent;
  }
  99% {
    color: transparent;
  }
  100% {
    color: red;
  }
`;

const FlashingRed = styled("div")`
  float: left;
  animation: ${flashRedAnim} 1.5s infinite;
`;

const TimeElapsedContainer = styled(Box)`
  position: relative;
  float: right;
  height: 0;
`;

const TimeElapsed = styled(Box)`
  color: red;
  padding: 0.5rem;
`;

const tryMimeTypes = ["video/webm", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm;codecs=h264"];

const findSupportedVideoCodec = () => {
  const type = tryMimeTypes.find(MediaRecorder.isTypeSupported);
  if (type) {
    return { mimeType: type };
  } else {
    console.log("No supported video format found");
    return error();
  }
};

function getUserMedia(callback: (err: any | undefined, stream: MediaStream | undefined) => void) {
  const getUserMedia_ =
    // @ts-ignore
    navigator.getUserMedia ||
    // @ts-ignore
    navigator.webkitGetUserMedia ||
    // @ts-ignore
    navigator.mozGetUserMedia ||
    // @ts-ignore
    navigator.msGetUserMedia;
  // @ts-ignore typescript thinks this is always defined, but it isn't necessarily
  if (getUserMedia) {
    getUserMedia_.call(
      navigator,
      { video: { facingMode: "user" }, audio: true },
      (stream: MediaStream | undefined) => callback(undefined, stream),
      (error: any | undefined) => callback(error, undefined)
    );
  } else {
    callback("No webcam browser support found", undefined);
  }
}

enum Stage {
  WebcamError,
  Ready,
  Recording,
  Reviewing,
}

interface State {
  elapsed: number;
  stage: Stage;
}

interface Props {
  maxDuration: number;
  onGotVideo: (blob: Blob) => void;
  onCannotRecord?: (reason: "NotAllowed" | "BrowserUnsupported" | "SetupFailed") => void;
  nextButtonText: JSX.Element | string;
}

// Note that on almost all browsers, video capture is only permitted over https
export class VideoRecorder extends React.Component<Props, State> {
  private video: HTMLVideoElement | undefined;
  private streamRecorder: MediaRecorder | undefined;
  private webcamStream: MediaStream | undefined;
  private recordedBlobs: Blob[] = [];
  private fullVideo: Blob | undefined = undefined;
  private maxDuration: number;
  private timer: number | undefined;
  // for browsers that don't support MediaRecorder (eg Safari < 13)
  private legacyRecorder: boolean;

  constructor(props: Props) {
    super(props);
    this.setState({ elapsed: 0, stage: Stage.Ready });
    this.maxDuration = props.maxDuration;
    this.legacyRecorder = browser_detect.isSafari || window.MediaRecorder === undefined;

    // Some browsers are just not supported
    if (browser_detect.isIE || browser_detect.isEdge) {
      this.onFailure("unsupported browser", "BrowserUnsupported");
    }
  }

  componentDidMount() {
    this.attachWebcamStream();
  }

  onFailure(msg: string, type: "BrowserUnsupported" | "NotAllowed" | "SetupFailed") {
    console.log(msg);
    this.setState({ ...this.state, stage: Stage.WebcamError });
    if (this.props.onCannotRecord) {
      this.props.onCannotRecord(type);
    }
    // send to backend for useful developer pondering
    reportVideoRecorderError.call({ msg, status: type });
  }

  attachRecorderToVideoElement = () => {
    if (this.video && this.webcamStream) {
      this.video.srcObject = this.webcamStream;
      const options = findSupportedVideoCodec();
      const streamRecorder = new MediaRecorder(this.video.srcObject as MediaStream, options);
      this.streamRecorder = streamRecorder;
      this.streamRecorder.ondataavailable = this.onDataAvailable;
      this.video.controls = false;
      this.video.src = "";
      this.video.muted = true;
    }
  };

  attachWebcamStream() {
    if (this.legacyRecorder) return;

    if (this.video) {
      getUserMedia((error, stream) => {
        if (error) {
          if (error.name === "SecurityError" || error.name === "NotAllowedError") {
            this.setState({ ...this.state, stage: Stage.WebcamError });
            this.onFailure("Permission denied", "NotAllowed");
          } else {
            this.onFailure(`error calling getUserMedia: ${error}`, "SetupFailed");
          }
        } else {
          this.webcamStream = stream;
          this.attachRecorderToVideoElement();
        }
      });
    } else {
      // could only happen if there is an initialization bug
      error();
    }
  }

  setRecordedBlob(fullVideo: Blob) {
    this.fullVideo = fullVideo;

    if (this.video) {
      this.video.srcObject = null;
      this.video.src = window.URL.createObjectURL(fullVideo);
      this.video.pause();
      this.video.muted = false;
      this.video.controls = true;
    }
  }

  onDataAvailable = (e: BlobEvent) => {
    if (!this.video) return;
    if (e.data && e.data.size > 0) {
      this.recordedBlobs.push(e.data);
      if (this.state.stage !== Stage.Recording) {
        this.setRecordedBlob(new Blob(this.recordedBlobs, { type: this.recordedBlobs[0].type }));
      }
    }
  };

  startRecording = () => {
    this.recordedBlobs = [];
    if (this.video && this.streamRecorder) {
      try {
        this.streamRecorder.start(this.maxDuration * 1000); // in ms, hence *1000
        this.setState({ ...this.state, stage: Stage.Recording });

        // Don't let the use submit videos exceeding the maxDuration
        this.timer = window.setInterval(() => {
          const elapsed = this.state.elapsed + 1;
          this.setState({ ...this.state, elapsed });
          if (elapsed >= this.maxDuration) {
            this.stopRecording();
          }
        }, 1000);
      } catch (e) {
        this.onFailure(`Error while creating MediaRecorder: ${e}`, "SetupFailed");
      }
    } else {
      this.onFailure("failed to make recorder", "SetupFailed");
    }
  };

  stopRecording = () => {
    if (this.streamRecorder) {
      this.streamRecorder.stop();
      this.setState({ ...this.state, stage: Stage.Reviewing });
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = undefined;
      }
    } else {
      error();
    }
  };

  tryAgain = () => {
    if (this.video) {
      this.attachRecorderToVideoElement();
      this.setState({ ...this.state, stage: Stage.Ready });
    }
  };

  renderRecordRedBlobOverlay = () => (
    <>
      <TimeElapsedContainer>
        <TimeElapsed>{formatSeconds(this.state.elapsed)}</TimeElapsed>
      </TimeElapsedContainer>
      <Box sx={{ position: "absolute", margin: "0.5rem" }}>
        <FlashingRed>&#11044; Recording</FlashingRed>
      </Box>
    </>
  );

  pushVideoBlobToParent() {
    this.props.onGotVideo(this.fullVideo || bug());
  }

  legacyVideoAttach(inputElem: HTMLInputElement) {
    if (inputElem.files) {
      this.recordedBlobs = [inputElem.files[0]];
      this.setRecordedBlob(inputElem.files[0]);
      this.setState({ ...this.state, stage: Stage.Reviewing });
    } else {
      error("Expected a file");
    }
  }

  recorderReadyWidget() {
    if (this.legacyRecorder) {
      return (
        <>
          <form action="#" method="post">
            <FormControl fullWidth>
              <InputLabel htmlFor="legacyVideoAttachHorror">Choose file</InputLabel>
              <Input
                type="file"
                id="legacyVideoAttachHorror"
                onChange={(e) => this.legacyVideoAttach(e.target as HTMLInputElement)}
                name="video"
                inputProps={{ accept: "video/*", capture: "capture" }}
              />
            </FormControl>
          </form>
        </>
      );
    } else {
      return (
        <>
          <Box>
            <Typography variant="body1">Press record when you are ready.</Typography>
            <Button
              sx={{
                backgroundColor: memberTheme.palette.greys.grey300,
                color: memberTheme.palette.primary.dark,
              }}
              onClick={this.startRecording}
              fullWidth
            >
              <PlayCircleOutlineIcon sx={{ marginRight: 1 }} />
              Record
            </Button>
            <Typography variant="body1">Please note - 60 second clip</Typography>
          </Box>
        </>
      );
    }
  }

  reviewVideoMessage() {
    if (this.legacyRecorder) {
      return "You can review your recording, and either submit now, or try uploading again.";
    } else {
      return "You can review your recording, and either upload now, or try recording again.";
    }
  }

  render() {
    return (
      <>
        {this.state.stage === Stage.Recording && <this.renderRecordRedBlobOverlay />}
        <video style={{ width: "100%" }} ref={(c) => (this.video = c as HTMLVideoElement)} autoPlay={true} muted />
        <div style={{ textAlign: "center" }}>
          {this.state.stage === Stage.Ready && this.recorderReadyWidget()}
          {this.state.stage === Stage.Recording ? (
            <Button
              sx={{
                backgroundColor: memberTheme.palette.greys.grey300,
                width: "100%",
                color: memberTheme.palette.primary.dark,
              }}
              onClick={this.stopRecording}
            >
              Stop
            </Button>
          ) : null}
          {this.state.stage === Stage.Reviewing && (
            <>
              <Typography variant="body1" gutterBottom>
                {this.reviewVideoMessage()}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <Button variant="contained" color="secondary" size="small" onClick={this.tryAgain}>
                  Try again
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={false}
                  onClick={() => this.pushVideoBlobToParent()}
                >
                  {this.props.nextButtonText}
                </Button>
              </Box>
            </>
          )}
        </div>
      </>
    );
  }
}

export function CannotRecordReason(props: { reason: "NotAllowed" | "BrowserUnsupported" | "SetupFailed" }) {
  switch (props.reason) {
    case "NotAllowed":
      return (
        <>
          <Typography align="center" variant="body1">
            <Text id="plugin.video_submission.camera_permission_refused_by_user">
              You have not allowed this site to use your webcam and microphone. You won&apos;t be able to record a
              message unless you allow this.
            </Text>
          </Typography>
          <Typography align="center" variant="body1">
            <Text id="plugin.video_submission.how_to_change_webcam_permissions">
              To allow the use of your webcam and microphone, find the padlock icon at the top left of your browser
              (left of the location bar), tap that, and site settings will open.
            </Text>
          </Typography>
        </>
      );
    case "BrowserUnsupported":
      return (
        <>
          <Typography align="center" variant="body1">
            Unfortunately it is not possible to record videos with your web browser software. If you would still like to
            help with this campaign then try opening this page in a supported web browser, like{" "}
            <a href="https://www.mozilla.org/en-US/firefox/new/">Mozilla Firefox</a> or{" "}
            <a href="https://www.google.com/chrome/">Google Chrome</a>.
          </Typography>
        </>
      );
    case "SetupFailed":
      return (
        <>
          <Typography align="center" variant="body1">
            Your webcam doesn&apos;t seem to be working.
          </Typography>
          <Typography align="center" variant="body1">
            Close any other apps that may be using the camera or microphone on your device.
          </Typography>
        </>
      );
  }
}
